// "Add a rule" form embedded in the alerts widget. The form lets a user
// pick one of the three MVP types and accept the §6.3.6 default
// threshold; tuning happens after creation via the per-row threshold
// input feature.

import { useState } from "react";

import {
  ALERT_RULE_DEFAULTS,
  ALERT_TYPES_MVP,
  type AlertTypeMvp,
} from "@community-analytics/shared";

import {
  ALERT_TYPE_LABELS,
  formatThresholdValue,
  useCreateAlertRuleMutation,
} from "@entities/alert-rule";

import { Button } from "@shared/ui";

export interface AlertRuleCreateFormProps {
  communityId: string | null;
  /** Types the owner already has rules for — those are excluded from the picker. */
  existingTypes: AlertTypeMvp[];
}

export function AlertRuleCreateForm({
  communityId,
  existingTypes,
}: AlertRuleCreateFormProps) {
  const create = useCreateAlertRuleMutation(communityId);
  const available = ALERT_TYPES_MVP.filter(
    (t) => !existingTypes.includes(t),
  );
  const [selected, setSelected] = useState<AlertTypeMvp | "">(
    available[0] ?? "",
  );

  if (available.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        All alert types are configured for this community.
      </p>
    );
  }

  const onAdd = () => {
    if (!selected) return;
    const def = ALERT_RULE_DEFAULTS[selected];
    create.mutate({ type: selected, threshold: def.threshold });
  };

  const previewThreshold = selected
    ? formatThresholdValue(selected, ALERT_RULE_DEFAULTS[selected].threshold)
    : null;
  const disabled =
    communityId === null || create.isPending || selected === "";

  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-4">
      <span className="text-sm font-medium">Add an alert rule</span>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={selected}
          onChange={(e) => setSelected(e.target.value as AlertTypeMvp | "")}
          aria-label="Alert rule type"
        >
          {available.map((t) => (
            <option key={t} value={t}>
              {ALERT_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground">
          Default threshold: {previewThreshold}
        </span>
        <Button
          type="button"
          size="sm"
          onClick={onAdd}
          disabled={disabled}
        >
          Add
        </Button>
      </div>
      {create.isError ? (
        <span role="alert" className="text-xs text-destructive">
          Could not create the rule. Try again.
        </span>
      ) : null}
    </div>
  );
}
